import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Reveal from "./Reveal";

const DESTINATIONS = [
  {
    id: "botswana",
    name: "Botswana",
    region: "Okavango Delta & Chobe",
    image: "https://images.unsplash.com/photo-1600521605615-a8d3a23d8262?crop=entropy&cs=srgb&fm=jpg&q=85",
    highlight: "Untamed waterways, private safari reserves, world-class conservation lodges.",
    stats: [["68+", "Curated listings"], ["12", "Private reserves"], ["Jun–Oct", "Peak season"]],
  },
  {
    id: "namibia",
    name: "Namibia",
    region: "Sossusvlei & Skeleton Coast",
    image: "https://images.unsplash.com/photo-1639402479478-f5e7881c0ccc?crop=entropy&cs=srgb&fm=jpg&q=85",
    highlight: "Sculpted ochre dunes, stargazing retreats, luxury fly-in expeditions.",
    stats: [["30+", "Desert lodges"], ["300+", "Days of sun"], ["Apr–Nov", "Peak season"]],
  },
  {
    id: "south-africa",
    name: "South Africa",
    region: "Cape Town, Kruger & Winelands",
    image: "https://images.unsplash.com/photo-1529528070131-eda9f3e90919?crop=entropy&cs=srgb&fm=jpg&q=85",
    highlight: "Cosmopolitan coastal luxury, Michelin dining, private game reserves.",
    stats: [["120+", "City & safari stays"], ["19", "National parks"], ["Year-round", "Peak season"]],
  },
  {
    id: "zimbabwe",
    name: "Zimbabwe",
    region: "Victoria Falls & Mana Pools",
    image: "https://images.unsplash.com/photo-1600521675573-6947b3dfd106?crop=entropy&cs=srgb&fm=jpg&q=85",
    highlight: "Thundering natural wonder, walking safaris, boutique riverfront camps.",
    stats: [["25+", "Falls & river camps"], ["5", "World Heritage sites"], ["May–Sep", "Peak season"]],
  },
];

export default function Destinations() {
  const [active, setActive] = useState(DESTINATIONS[0]);

  return (
    <section id="destinations" className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:px-16 lg:py-32">
      <Reveal>
        <p className="kicker mb-6">Destinations</p>
        <h2 className="max-w-2xl font-serif text-3xl font-semibold leading-snug tracking-tight sm:text-4xl lg:text-5xl">
          Four countries. <span className="text-gold">One marketplace.</span>
        </h2>
      </Reveal>

      <Reveal delay={0.1}>
        <div className="mt-10 flex flex-wrap gap-3">
          {DESTINATIONS.map((d) => (
            <button
              key={d.id}
              data-testid={`destination-tab-${d.id}`}
              onClick={() => setActive(d)}
              className={`rounded-full border px-6 py-2.5 font-mono text-xs uppercase tracking-[0.2em] transition-[border-color,background-color,color] duration-300 ${
                active.id === d.id
                  ? "border-gold bg-gold/10 text-gold"
                  : "border-line text-muted hover:border-gold/40 hover:text-cream"
              }`}
            >
              {d.name}
            </button>
          ))}
        </div>
      </Reveal>

      <Reveal delay={0.15}>
        <div className="mt-10 grid gap-10 lg:grid-cols-12">
          <div className="relative overflow-hidden rounded-2xl border border-goldline lg:col-span-7">
            <AnimatePresence mode="wait">
              <motion.img
                key={active.id}
                src={active.image}
                alt={active.name}
                initial={{ opacity: 0, scale: 1.06 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className="photo-treat h-80 w-full object-cover sm:h-[460px]"
              />
            </AnimatePresence>
            <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">{active.region}</p>
              <p className="mt-1 font-serif text-4xl font-semibold text-cream">{active.name}</p>
            </div>
          </div>

          <div className="flex flex-col justify-between lg:col-span-5">
            <AnimatePresence mode="wait">
              <motion.div
                key={active.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.5 }}
              >
                <p className="text-lg leading-relaxed text-muted">{active.highlight}</p>
                <div className="mt-10 space-y-0 divide-y divide-line border-y border-line">
                  {active.stats.map(([v, l]) => (
                    <div key={l} className="flex items-baseline justify-between py-4">
                      <span className="font-serif text-2xl text-gold">{v}</span>
                      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-subtle">{l}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>
            <a href="#waitlist" className="btn-ghost mt-10 w-fit">
              List your {active.name} property
            </a>
          </div>
        </div>
      </Reveal>
    </section>
  );
}
